import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Briefcase,
  Clock,
  Users,
  ArrowRight,
  Star,
  Calendar,
  Shield
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const OfficeCleaningPage = () => {
  const services = [
    {
      title: "Recurring Office Cleaning",
      description: "Regular maintenance cleaning on your schedule—daily, weekly, bi-weekly, or monthly. Consistent service with the same team."
    },
    {
      title: "Professional Office Suites",
      description: "Law firms, accounting practices, consulting offices, and professional service providers who need reliable, discreet cleaning."
    },
    {
      title: "Coworking & Shared Spaces",
      description: "Common areas, private offices, conference rooms, and break rooms. Flexible scheduling to work around members."
    },
    {
      title: "Small Business Offices",
      description: "Local businesses, startup offices, and small teams who need dependable cleaning without complicated contracts."
    }
  ];

  const whatWeClean = [
    "Desks, workstations, and surfaces",
    "Conference rooms and meeting spaces",
    "Break rooms and kitchens",
    "Restrooms and common areas",
    "Floors (vacuum, mop, spot clean)",
    "Trash removal and recycling",
    "Entry areas and lobbies",
    "Glass doors and interior windows"
  ];

  const whyChoose = [
    "Same team every visit—consistency you can count on",
    "Flexible scheduling, including after-hours service",
    "Direct communication with Bob and Heather",
    "Locally owned, not a franchise",
    "15+ years of professional cleaning experience",
    "Insured and bonded for your protection"
  ];

  const faqs = [
    {
      question: "What does recurring office cleaning include?",
      answer: "Our recurring office cleaning covers desks and workstations, conference rooms, break rooms, restrooms, floors (vacuuming and mopping), trash removal, and common areas. We tailor the service to your office's needs and can adjust the checklist based on your priorities."
    },
    {
      question: "Can you clean outside of business hours?",
      answer: "Yes. Many of our office clients prefer evening or early morning cleaning to avoid disrupting their workday. We're flexible and work with your schedule."
    },
    {
      question: "Do we get the same cleaning team each time?",
      answer: "Yes. You'll have the same cleaners every visit. They'll learn your office, your preferences, and become familiar with your space. That consistency is one of the biggest reasons our commercial clients stick with us long-term."
    },
    {
      question: "How do you handle keys and building access?",
      answer: "We're flexible and professional. Whether it's a key, lockbox, building code, or coordinating with property management, we'll work with your access system. We're insured and bonded, and we take security seriously."
    },
    {
      question: "What if we need to adjust the schedule or scope of work?",
      answer: "Just call or text us. As a small, locally-owned business, we're easy to reach and responsive. If your needs change—more frequent service, less frequent, or adjusting what gets cleaned—we'll work with you."
    },
    {
      question: "Do you provide cleaning supplies and equipment?",
      answer: "Yes. We bring all necessary cleaning supplies and equipment. If your office has specific product preferences or requirements, let us know and we'll accommodate."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Office Cleaning Services in Brevard County, FL | Pierce Premier",
      description: "Professional office cleaning in Brevard County. Recurring service, flexible scheduling, same team every visit. Trusted by local businesses. Call 321-408-1347.",
      url: "/services/commercial/office-cleaning"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Commercial Cleaning", url: "/services/commercial" },
      { name: "Office Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Office Cleaning Services",
      description: "Professional recurring office cleaning for Brevard County businesses. Flexible scheduling, consistent team, reliable service."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="Office Cleaning Services in Brevard County, FL | Pierce Premier"
        description="Professional office cleaning in Brevard County. Recurring service, flexible scheduling, same team every visit. Trusted by local businesses. Call 321-408-1347."
        canonical="/services/commercial/office-cleaning"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  <Badge className="bg-blue-600 text-white">Professional Service</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Office Cleaning Services in Brevard County
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Your office reflects your business. Whether you're a professional service firm, a coworking space, or a small business team, Pierce Premier provides dependable recurring cleaning that keeps your workspace presentable and professional—without the hassle of managing it yourself.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  We're Bob and Heather—a husband-and-wife team serving Brevard County businesses. You'll work directly with us, have the same cleaning team every visit, and get responsive communication when you need it. We've cleaned professional offices, small business suites, and shared workspaces throughout the county.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Request Walk-Through
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional office cleaning services in Brevard County, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Brevard County Businesses Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team, Every Visit</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews or strangers in your office. You'll have the same cleaners every time who learn your space, your preferences, and deliver consistent results. That familiarity matters.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Flexible Scheduling</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We work around your business hours. Evening cleaning, early morning service, or customized schedules—we're flexible and reliable, so your office gets cleaned without disrupting your team.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Accountable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're not a franchise—we're a local business that stands behind our work. You'll always know who to call, and we respond quickly.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience to every commercial property. We understand professional standards and deliver quality you can count on.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  How Office Cleaning Works
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Initial Walk-Through</h3>
                      <p className="text-gray-600">We visit your office, discuss what needs to be cleaned, review your schedule preferences, and answer your questions. No pressure, no complicated contracts.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Customized Service Plan</h3>
                      <p className="text-gray-600">We create a cleaning plan tailored to your office. Frequency, scope, timing—it's all based on what works for you, not a one-size-fits-all template.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Consistent, Reliable Service</h3>
                      <p className="text-gray-600">The same team cleans your office on schedule. You'll have our direct contact info, and if anything needs adjusting, just reach out—we're responsive and easy to work with.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Office Types We Serve
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-blue-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What We Clean
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whatWeClean.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-blue-100">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Businesses Work With Us Long-Term
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whyChoose.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Service Areas
              </h2>
              <p className="text-gray-600">Serving businesses throughout Brevard County</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Palm Bay", link: "/areas/palm-bay" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Rockledge", link: "/areas/rockledge" },
                { name: "Cocoa", link: "/areas/cocoa" },
                { name: "Merritt Island", link: "/areas/merritt-island" }
              ].map((city, idx) => (
                <Link key={idx} to={city.link} className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 hover:border-blue-400 transition-colors text-center group">
                  <span className="text-blue-700 font-semibold group-hover:text-blue-900">{city.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-blue-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Office Clients
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Related Commercial Services
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/services/commercial/property-management" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">Property Management Cleaning</h3>
                <p className="text-gray-600 text-sm">Turnover cleaning and common area maintenance for multi-unit properties</p>
              </Link>
              <Link to="/services/commercial/construction-cleanup" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">Post-Construction Cleaning</h3>
                <p className="text-gray-600 text-sm">Final cleanup for new construction and renovation projects</p>
              </Link>
            </div>
          </div>
        </section>

        <CTASection 
          title="Let's Talk About Your Office Cleaning Needs"
          description="Schedule a walk-through and get a straightforward estimate. No complicated contracts—just reliable cleaning on your schedule."
          primaryButtonText="Request Walk-Through & Estimate"
        />
      </div>
    </>
  );
};

export default OfficeCleaningPage;
